// src/components/auth/EmailPasswordLoginForm.jsx
// -----------------------------------------------------------------------------
// Reusable email/password login form.
//
// Purpose:
// - Render modal-safe login UI using standard MUI Dialog hierarchy.
// - Reuse useEmailPasswordLogin for all Firebase sign-in behavior.
// - Avoid navigation, redirects, asset fetching, or route assumptions.
// - Allow future Login.jsx refactor to use the same form/hook.
// - Keep the Asset Library modal accessible with MUI Dialog semantics.
// -----------------------------------------------------------------------------

import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material';

import useEmailPasswordLogin from '../../hooks/useEmailPasswordLogin';

const DEFAULT_COPY = {
  title: 'Sign in to access Asset Studio',
  description:
    'Log in to your organization account to choose an asset from your ' +
    'library and attach it to this form.',
  emailLabel: 'Email',
  passwordLabel: 'Password',
  rememberMeLabel: 'Remember Me',
  submitLabel: 'Sign in',
  cancelLabel: 'Cancel',
  resolvingLabel: 'Signing in…',
};

export default function EmailPasswordLoginForm({
  copy,
  onSuccess,
  onCancel,
  autoFocus = true,
  titleId = 'asset-login-dialog-title',
  descriptionId = 'asset-login-dialog-description',
  formId = 'asset-library-inline-login-form',
}) {
  const mergedCopy = useMemo(
    () => ({
      ...DEFAULT_COPY,
      ...(copy || {}),
    }),
    [copy],
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const {
    error,
    isBusy,
    isResolvingAuth,
    login,
  } = useEmailPasswordLogin({
    onSuccess,
  });

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      await login({
        email,
        password,
        rememberMe,
      });
    },
    [
      email,
      login,
      password,
      rememberMe,
    ],
  );

  const busy = isBusy || isResolvingAuth;

  return (
    <>
      <DialogTitle
        id={titleId}
        sx={{
          px: 3,
          pt: 3,
          pb: 1,
          fontWeight: 700,
          lineHeight: 1.25,
        }}
      >
        {mergedCopy.title}
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pt: 0,
          pb: 2,
        }}
      >
        <DialogContentText
          id={descriptionId}
          component="p"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.6,
            mb: 2.5,
          }}
        >
          {mergedCopy.description}
        </DialogContentText>

        <Box
          component="form"
          id={formId}
          onSubmit={handleSubmit}
          noValidate
        >
          <Stack spacing={2}>
            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}

            <TextField
              id={`${formId}-email`}
              name="email"
              label={mergedCopy.emailLabel}
              type="email"
              required
              fullWidth
              autoFocus={autoFocus}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={busy}
              autoComplete="email"
            />

            <TextField
              id={`${formId}-password`}
              name="password"
              label={mergedCopy.passwordLabel}
              type="password"
              required
              fullWidth
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={busy}
              autoComplete="current-password"
            />

            <FormControlLabel
              control={
                <Checkbox
                  id={`${formId}-remember-me`}
                  name="rememberMe"
                  checked={rememberMe}
                  onChange={(event) => setRememberMe(event.target.checked)}
                  disabled={busy}
                />
              }
              label={mergedCopy.rememberMeLabel}
            />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 0,
          gap: 1,
        }}
      >
        {typeof onCancel === 'function' && (
          <Button
            variant="text"
            color="inherit"
            onClick={onCancel}
            disabled={busy}
          >
            {mergedCopy.cancelLabel}
          </Button>
        )}

        <Button
          type="submit"
          form={formId}
          variant="contained"
          disabled={busy}
          startIcon={
            busy ? <CircularProgress size={18} /> : undefined
          }
        >
          {busy ? mergedCopy.resolvingLabel : mergedCopy.submitLabel}
        </Button>
      </DialogActions>
    </>
  );
}

EmailPasswordLoginForm.propTypes = {
  copy: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    emailLabel: PropTypes.string,
    passwordLabel: PropTypes.string,
    rememberMeLabel: PropTypes.string,
    submitLabel: PropTypes.string,
    cancelLabel: PropTypes.string,
    resolvingLabel: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  autoFocus: PropTypes.bool,
  titleId: PropTypes.string,
  descriptionId: PropTypes.string,
  formId: PropTypes.string,
};